/**
 * Parser for the สปสช. "รายงานสรุปการขอเบิกชดเชยค่าการรักษาผู้ป่วยของหน่วยบริการ" .xlsx
 * (the e-Claim / NHSO export, one file per หน่วยบริการ). Pure — takes the sheets that
 * `readXlsx()` returns, so it is testable without a file.
 *
 * THE FOUR TABS, and which one carries the money:
 *   Summary     one row PER REP — รหัสหน่วยบริการ, REP NO., REP Date, then counts + baht
 *               for เรียกเก็บ / ชดเชย / ไม่ชดเชย and the อุทธรณ์ block. Used as the
 *               INTEGRITY CHECK, like the รวม footer on the ปกส. PDF.
 *   Detail      one row PER PERSON per REP — counts and totals only, NO service date.
 *               Cannot be matched to a billing line on its own; used as a second check.
 *   Individual  one row PER CLAIMED ITEM — REP, Trans ID, PID (เลขบัตร), name, สิทธิ,
 *               HCODE, service date, item, เรียกเก็บ, PS penalty, ชดเชย / ไม่ชดเชย /
 *               จ่ายเพิ่ม / เรียกคืน, สถานะ, deny code, STMID. ⚠️ THIS is the tab that
 *               reconciles — it is the only one with a service date and a per-visit amount.
 *   DENY CODE   code → meaning, read so a rejected row can say WHY in Thai.
 *
 * ⚠️ COLUMNS ARE FOUND BY HEADER TEXT, never by letter. The header is three rows deep
 * with merged groups (เรียกเก็บ → จำนวน / ราคาต่อหน่วย / ราคาเพดาน / รวมเงินที่ขอเบิก),
 * and the portal has already shipped the Summary sheet with a mislabelled "ATK" block
 * that duplicates the real figures — positional reading is one template change away
 * from reading the wrong column with total confidence.
 *
 * ⚠️ DATES ARE MIXED ERA, like the ปกส. report. Row and REP dates read "24/08/2026"
 * (AD); the print stamp reads "16/10/2569 เวลา 11:10" (BE). `dmyToISO` converts any
 * year above 2400 from BE, so either is safe, and nothing is "corrected" by guessing.
 *
 * ⚠️ IN THE REPORT IS NOT THE SAME AS PAID IN FULL. Every row carries its own outcome:
 * ชดเชย (compensated), ไม่ชดเชย with a deny code, a PS late-submission cut, or a
 * เรียกคืน clawback on appeal. `verdict` reduces that to one word the page acts on, and
 * only `paid` — compensated in full, nothing denied or clawed back — is ever written.
 */

const pad = (n) => String(n).padStart(2, '0')

/** "24/08/2026" | "16/10/2569 เวลา 11:10" → "2026-08-24". BE years are converted. */
export function dmyToISO(v) {
  const m = /(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(String(v ?? ''))
  if (!m) return ''
  let y = Number(m[3])
  if (y > 2400) y -= 543
  const mo = Number(m[2]); const d = Number(m[1])
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return ''
  return `${y}-${pad(mo)}-${pad(d)}`
}

/** 100 | "   100.00" | "1,234.50" → number. Blank → 0 (the export pads zeros, never blanks money). */
export const money = (v) => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0
  const n = Number(String(v ?? '').replace(/[,\s฿]/g, ''))
  return Number.isFinite(n) ? n : 0
}

const str = (v) => String(v ?? '').replace(/ /g, ' ').trim()
const norm = (v) => str(v).replace(/\s+/g, '').toLowerCase()
const digits = (v) => String(v ?? '').replace(/\D/g, '')
const round2 = (n) => Math.round(n * 100) / 100

/** A REP number: DINS69082400068 — letters then a long digit run. Also what tells a
 *  data row apart from the glossary printed under the Individual table. */
const REP_RE = /^[A-Z]{2,8}\d{6,}$/

/* ── header location ─────────────────────────────────────────────────────── */

/** First row index (within the first 30) that holds every one of `labels`. */
function findHeaderRow(rows, labels) {
  const want = labels.map(norm)
  for (let i = 0; i < Math.min(rows.length, 30); i++) {
    const cells = (rows[i] ?? []).map(norm)
    if (want.every((w) => cells.includes(w))) return i
  }
  return -1
}

/** Column whose cell in `row` matches `label` — exact, or `contains` when asked. The
 *  n-th occurrence for labels that repeat (ลำดับที่ appears twice in Individual). */
function col(row, label, { contains = false, nth = 0 } = {}) {
  const w = norm(label)
  let seen = 0
  for (let i = 0; i < (row ?? []).length; i++) {
    const c = norm(row[i])
    if (c && (contains ? c.includes(w) : c === w)) {
      if (seen === nth) return i
      seen++
    }
  }
  return -1
}

/** A label written in the first column of the page header ("รายงาน : ", "ชื่อหน่วยบริการ : ") → the value next to it. */
function labelValue(rows, label) {
  const w = norm(label)
  for (let i = 0; i < Math.min(rows.length, 10); i++) {
    const r = rows[i] ?? []
    for (let c = 0; c < r.length - 1; c++) {
      if (norm(r[c]).replace(/:$/, '') === w) return str(r[c + 1])
    }
  }
  return ''
}

/* ── verdict ─────────────────────────────────────────────────────────────── */

/**
 * One row's outcome, in the order that matters for money:
 *   clawback — เรียกคืน > 0: สปสช. is taking money BACK. Never auto-written.
 *   denied   — nothing compensated (ไม่ชดเชย, or a zero ชดเชย for any reason).
 *   partial  — compensated, but less than claimed (PS late cut, ceiling, part-deny).
 *   paid     — compensated in full. The only verdict the importer writes.
 * The STATUS WORDING is deliberately not the gate — amounts are. An unseen status string
 * with full compensation is still paid; a "ชดเชย" row with a short amount is still partial.
 */
export function nhsoVerdict(r) {
  if (money(r.clawback) > 0) return 'clawback'
  if (money(r.compensated) <= 0) return 'denied'
  if (money(r.compensated) + 0.005 < money(r.amount) || money(r.denied) > 0) return 'partial'
  return 'paid'
}

/* ── the parser ──────────────────────────────────────────────────────────── */

/**
 * @param {{[sheet: string]: any[][]}} sheets  from readXlsx()
 * @returns {{header, reps, rows, denyCodes, checks, warnings, errors}}
 *   errors   → the file must not be reconciled (the page disables ตรวจสอบ)
 *   warnings → shown, not blocking
 */
export function parseNhsoWorkbook(sheets) {
  const errors = []
  const warnings = []
  const byName = new Map(Object.entries(sheets ?? {}).map(([k, v]) => [norm(k), v]))
  const sheet = (name) => byName.get(norm(name)) ?? null

  const ind = sheet('Individual')
  const sum = sheet('Summary')
  const det = sheet('Detail')
  const deny = sheet('DENY CODE')

  if (!ind) {
    return {
      header: {}, reps: [], rows: [], denyCodes: {}, checks: {}, warnings,
      errors: ['ไม่พบแท็บ Individual — ไฟล์นี้ไม่ใช่รายงานสรุปการขอเบิกชดเชยจาก สปสช.'],
    }
  }

  /* deny codes ------------------------------------------------------------- */
  const denyCodes = {}
  for (const r of deny ?? []) {
    const code = str(r?.[0]); const desc = str(r?.[1])
    if (/^[A-Z]{1,4}\d{2,4}$/.test(code) && desc) denyCodes[code] = desc
  }

  /* Summary — one row per REP ---------------------------------------------- */
  const reps = []
  if (sum) {
    const h = findHeaderRow(sum, ['REP NO.', 'REP Date'])
    if (h < 0) {
      errors.push('อ่านหัวตารางแท็บ Summary ไม่ได้')
    } else {
      const H = sum[h]
      const cRep = col(H, 'REP NO.')
      const cDate = col(H, 'REP Date')
      const cCode = col(H, 'รหัสหน่วยบริการ')
      const cName = col(H, 'ชื่อหน่วยบริการ')
      const cBatch = col(H, 'งวด/เลขที่เบิกจ่าย')
      // The ข้อมูลปกติ block: เรียกเก็บ / ชดเชย / ไม่ชดเชย, each a (รายการ, บาท) pair.
      const base = col(H, 'ข้อมูลปกติ')
      // The อุทธรณ์ block. Only its จ่ายเพิ่ม / เรียกคืน (ชดเชยสุทธิ) pairs matter here,
      // found under their own sub-headers rather than counted from the left.
      const appeal = col(H, 'ข้อมูลอุทธรณ์')
      const subs = sum.slice(h + 1, h + 6)
      const subCol = (label, from, to) => {
        for (const r of subs) {
          for (let i = from; i < to; i++) if (norm(r?.[i]) === norm(label)) return i
        }
        return -1
      }
      const appealEnd = appeal >= 0 ? appeal + 12 : -1
      const cExtra = appeal >= 0 ? subCol('จ่ายเพิ่ม', appeal, appealEnd) : -1
      const cClaw = appeal >= 0 ? subCol('เรียกคืน', appeal, appealEnd) : -1
      if (base < 0) errors.push('ไม่พบคอลัมน์ "ข้อมูลปกติ" ในแท็บ Summary')

      for (let i = h + 1; i < sum.length; i++) {
        const r = sum[i] ?? []
        const rep = str(r[cRep])
        if (!REP_RE.test(rep)) continue
        reps.push({
          rep_no: rep,
          rep_date: dmyToISO(r[cDate]),
          hcode: str(r[cCode]).toUpperCase(),
          clinic_name: str(r[cName]),
          batches: str(r[cBatch]),
          claim_count: base >= 0 ? money(r[base]) : null,
          claim_amount: base >= 0 ? money(r[base + 1]) : null,
          comp_count: base >= 0 ? money(r[base + 2]) : null,
          comp_amount: base >= 0 ? money(r[base + 3]) : null,
          deny_count: base >= 0 ? money(r[base + 4]) : null,
          deny_amount: base >= 0 ? money(r[base + 5]) : null,
          appeal_extra: cExtra >= 0 ? money(r[cExtra + 1]) : 0,
          appeal_clawback: cClaw >= 0 ? money(r[cClaw + 1]) : 0,
        })
      }
      if (!reps.length) errors.push('ไม่พบ REP ในแท็บ Summary')
    }
  } else {
    errors.push('ไม่พบแท็บ Summary — ตรวจยอดรวมไม่ได้')
  }

  /* Individual — one row per claimed item ---------------------------------- */
  const rows = []
  const h = findHeaderRow(ind, ['REP No.', 'Trans ID'])
  if (h < 0) {
    errors.push('อ่านหัวตารางแท็บ Individual ไม่ได้')
  } else {
    const H = ind[h]
    const S = ind[h + 1] ?? []
    const C = {
      seq: col(H, 'ลำดับที่'),
      rep: col(H, 'REP No.'),
      trans: col(H, 'Trans ID'),
      hn: col(H, 'HN'),
      pid: col(H, 'PID', { contains: true }),
      name: col(H, 'ชื่อ-สกุล'),
      rights: col(H, 'สิทธิการรักษาพยาบาล'),
      hcode: col(H, 'HCODE'),
      date: col(H, 'วันที่รับบริการ', { contains: true }),
      itemSeq: col(H, 'ลำดับที่', { nth: 1 }),
      item: col(H, 'รายการประเภทที่ขอเบิก'),
      status: col(H, 'สถานะ'),
      note: col(H, 'หมายเหตุ'),
      stmid: col(H, 'STMID', { contains: true }),
      // Sub-header row: the merged groups' children.
      qty: col(S, 'จำนวน'),
      amount: col(S, 'รวมเงินที่ขอเบิก'),
      psCode: col(S, 'PS CODE'),
      psPct: col(S, '%'),
      comp: col(S, 'ชดเชย'),
      denied: col(S, 'ไม่ชดเชย'),
      extra: col(S, 'จ่ายเพิ่ม'),
      claw: col(S, 'เรียกคืน'),
    }
    const required = { rep: 'REP No.', pid: 'PID', name: 'ชื่อ-สกุล', date: 'วันที่รับบริการ', amount: 'รวมเงินที่ขอเบิก', comp: 'ชดเชย' }
    const missing = Object.entries(required).filter(([k]) => C[k] < 0).map(([, l]) => l)
    if (missing.length) errors.push(`แท็บ Individual ไม่มีคอลัมน์: ${missing.join(', ')}`)

    if (!missing.length) {
      const repDate = new Map(reps.map((x) => [x.rep_no, x.rep_date]))
      for (let i = h + 1; i < ind.length; i++) {
        const r = ind[i] ?? []
        const rep = str(r[C.rep])
        // The glossary under the table ("DENY CODE", "ลำดับที่ …") has no REP number.
        if (!REP_RE.test(rep)) continue
        const service_date = dmyToISO(r[C.date])
        if (!service_date) {
          warnings.push(`แถวที่ ${i + 1}: อ่านวันที่รับบริการไม่ได้ (${str(r[C.date]) || 'ว่าง'}) — ข้ามแถวนี้`)
          continue
        }
        const pid = digits(r[C.pid])
        const note = C.note >= 0 ? str(r[C.note]) : ''
        const row = {
          seq: rows.length + 1,
          file_seq: C.seq >= 0 ? str(r[C.seq]) : '',
          rep_no: rep,
          rep_date: repDate.get(rep) ?? '',
          trans_id: C.trans >= 0 ? str(r[C.trans]) : '',
          hn: C.hn >= 0 ? str(r[C.hn]) : '',
          // Only a 13-digit PID is a national ID. Anything else (VCT/NAP numbers) matches
          // on name instead of being sent as an ID that can never hit.
          idcard: pid.length === 13 ? pid : '',
          pid,
          raw_name: str(r[C.name]),
          rights: C.rights >= 0 ? str(r[C.rights]) : '',
          hcode: C.hcode >= 0 ? str(r[C.hcode]).toUpperCase() : '',
          service_date,
          item_seq: C.itemSeq >= 0 ? str(r[C.itemSeq]) : '',
          item: C.item >= 0 ? str(r[C.item]) : '',
          qty: C.qty >= 0 ? money(r[C.qty]) : null,
          amount: money(r[C.amount]),
          ps_code: C.psCode >= 0 ? str(r[C.psCode]) : '',
          ps_pct: C.psPct >= 0 ? str(r[C.psPct]) : '',
          compensated: money(r[C.comp]),
          denied: C.denied >= 0 ? money(r[C.denied]) : 0,
          extra: C.extra >= 0 ? money(r[C.extra]) : 0,
          clawback: C.claw >= 0 ? money(r[C.claw]) : 0,
          status: C.status >= 0 ? str(r[C.status]) : '',
          note,
          // หมายเหตุ carries the deny code(s) on a ไม่ชดเชย row; there can be several.
          deny_codes: [...new Set(note.toUpperCase().match(/\b[A-Z]{1,4}\d{2,4}\b/g) ?? [])],
          stmid: C.stmid >= 0 ? str(r[C.stmid]) : '',
        }
        row.verdict = nhsoVerdict(row)
        rows.push(row)
      }
      if (!rows.length) errors.push('ไม่พบรายการในแท็บ Individual')
    }
  }

  /* integrity: Individual vs Summary, per REP ------------------------------- */
  // The baht must agree to the satang. A half-read sheet that still looks complete is
  // the one failure here that would write real money wrongly — so it blocks.
  const perRep = new Map()
  for (const r of rows) {
    if (!perRep.has(r.rep_no)) perRep.set(r.rep_no, { n: 0, amount: 0, comp: 0, denied: 0 })
    const t = perRep.get(r.rep_no)
    t.n += 1; t.amount += r.amount; t.comp += r.compensated; t.denied += r.denied
  }
  const repChecks = reps.map((x) => {
    const t = perRep.get(x.rep_no) ?? { n: 0, amount: 0, comp: 0, denied: 0 }
    const ok = (a, b) => b == null || Math.abs(round2(a) - round2(b)) < 0.005
    const amountOk = ok(t.amount, x.claim_amount)
    const compOk = ok(t.comp, x.comp_amount)
    const deniedOk = ok(t.denied, x.deny_amount)
    const countOk = x.claim_count == null || t.n === x.claim_count
    return {
      rep_no: x.rep_no, rows: t.n, amount: round2(t.amount), compensated: round2(t.comp), denied: round2(t.denied),
      summary_count: x.claim_count, summary_amount: x.claim_amount, summary_comp: x.comp_amount, summary_denied: x.deny_amount,
      ok: amountOk && compOk && deniedOk, count_ok: countOk,
    }
  })
  for (const c of repChecks) {
    if (!c.ok) {
      errors.push(`ยอด REP ${c.rep_no} ไม่ตรงกับแท็บ Summary — อ่านได้ เรียกเก็บ ${c.amount} / ชดเชย ${c.compensated} แต่ Summary ระบุ ${c.summary_amount} / ${c.summary_comp}`)
    } else if (!c.count_ok) {
      warnings.push(`REP ${c.rep_no}: จำนวนแถว ${c.rows} ไม่เท่ากับ Summary (${c.summary_count}) แต่ยอดเงินตรงกัน`)
    }
  }
  const repSet = new Set(reps.map((x) => x.rep_no))
  const orphanReps = [...perRep.keys()].filter((k) => !repSet.has(k))
  if (orphanReps.length && reps.length) {
    errors.push(`REP ${orphanReps.join(', ')} อยู่ในแท็บ Individual แต่ไม่อยู่ใน Summary`)
  }

  /* second check: Individual vs Detail, per (REP, person) ------------------- */
  let detailChecked = 0
  const detailMismatch = []
  if (det) {
    const dh = findHeaderRow(det, ['REP No.', 'ชื่อ-สกุล'])
    if (dh >= 0) {
      const DH = det[dh]
      const cRep = col(DH, 'REP No.')
      const cPid = col(DH, 'PID', { contains: true })
      // ⚠️ THE DATA ROWS ARE RAGGED. The header is 45 columns wide — seven program
      // groups of five, then ชดเชยสุทธิ (รวมจำนวนชดเชย / ไม่ชดเชย / จ่ายเพิ่ม / เรียกคืน)
      // in AP:AS — but a data row carries only the groups that apply to it, so on the
      // first real file it was 15 cells long and AP did not exist. Reading the total
      // under its header returned 0 for every person. The net block is always the
      // row's LAST FOUR cells, so the total is the fourth from the end.
      const hasTotals = det.slice(dh, dh + 3).some((r) => col(r, 'รวมจำนวนชดเชย', { contains: true }) >= 0)
      if (cRep >= 0 && cPid >= 0 && hasTotals) {
        const ours = new Map()
        for (const r of rows) {
          const k = `${r.rep_no}|${r.pid}`
          ours.set(k, (ours.get(k) ?? 0) + r.compensated)
        }
        for (let i = dh + 1; i < det.length; i++) {
          const r = det[i] ?? []
          const rep = str(r[cRep])
          if (!REP_RE.test(rep)) continue
          const filled = r.slice(0, r.length)
          while (filled.length && str(filled[filled.length - 1]) === '') filled.pop()
          if (filled.length < cPid + 5) continue
          const k = `${rep}|${digits(r[cPid])}`
          detailChecked++
          if (Math.abs(round2(ours.get(k) ?? 0) - round2(money(filled[filled.length - 4]))) >= 0.005) detailMismatch.push(k)
        }
      }
    }
    if (detailMismatch.length) {
      warnings.push(`ยอดรายคน ${detailMismatch.length} รายไม่ตรงกับแท็บ Detail — ตรวจไฟล์ก่อนบันทึก`)
    }
  }

  /* header ----------------------------------------------------------------- */
  const codes = [...new Set([...reps.map((x) => x.hcode), ...rows.map((r) => r.hcode)].filter(Boolean))]
  if (codes.length > 1) errors.push(`ไฟล์นี้มีหลายรหัสหน่วยบริการ (${codes.join(', ')}) — แยกไฟล์ทีละหน่วยบริการ`)
  if (!codes.length) errors.push('ไม่พบรหัสหน่วยบริการ (HCODE) ในไฟล์')

  const dates = rows.map((r) => r.service_date).sort()
  const header = {
    hcode: codes[0] ?? '',
    clinicName: labelValue(sum ?? ind, 'ชื่อหน่วยบริการ') || reps[0]?.clinic_name || '',
    printed_at: dmyToISO(labelValue(sum ?? ind, 'วันที่จัดพิมพ์รายงาน')),
    // The report has no window of its own — a REP is a submission batch, not a month.
    // The service dates it covers stand in for one.
    start_date: dates[0] ?? '',
    end_date: dates[dates.length - 1] ?? '',
  }

  if (reps.some((x) => x.appeal_extra || x.appeal_clawback)) {
    warnings.push('มีข้อมูลอุทธรณ์ (จ่ายเพิ่ม/เรียกคืน) ในไฟล์นี้ — ระบบไม่บันทึกส่วนนี้ให้ ตรวจเอง')
  }
  const missingDate = reps.filter((x) => !x.rep_date).map((x) => x.rep_no)
  if (missingDate.length) warnings.push(`REP ${missingDate.join(', ')} ไม่มี REP Date — รายการของ REP นี้จะไม่ถูกบันทึกรับเงิน`)

  const count = (v) => rows.filter((r) => r.verdict === v).length
  const total = (k) => round2(rows.reduce((t, r) => t + r[k], 0))

  return {
    header,
    reps,
    rows,
    denyCodes,
    checks: {
      reps: repChecks,
      detail_checked: detailChecked,
      detail_mismatch: detailMismatch.length,
      totals: {
        rows: rows.length,
        amount: total('amount'),
        compensated: total('compensated'),
        denied: total('denied'),
        paid: count('paid'), partial: count('partial'), denied_rows: count('denied'), clawback: count('clawback'),
      },
    },
    warnings,
    errors,
  }
}
